import {memo, useEffect, useRef, useState} from 'react'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import {ProgressivePullRequestDiffEntry} from './progressive-diff/ProgressivePullRequestDiffEntry'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useDiffEntriesDigestsWithFilter,
  useDiffSummaryCount,
  useProgressiveDiffActions,
} from '../stores/selectors'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'

export interface PullRequestDiffsListProps {
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const diffsParentRef = useRef<HTMLDivElement>(null)
  const diffEntryDigests = useDiffEntriesDigestsWithFilter()
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue} = useProgressiveDiffActions()

  useEffect(() => {
    startQueue({autoLoadAllDiffs: true})
  }, [startQueue])

  const {onScrollToAndFocusEntry, getTargetableEntriesMap} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()

  const totalVisibleEntries = diffEntryDigests.length
  const shouldShowWhimsy = totalVisibleEntries >= 15

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (diffEntryDigests.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc data-testid="progressive-diffs-list" className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      {diffEntryDigests.map(pathDigest => {
        return (
          <div
            key={pathDigest}
            ref={(node: HTMLDivElement) => {
              const map = getTargetableEntriesMap()
              if (node) {
                map.set(pathDigest, node)
              } else {
                map.delete(pathDigest)
              }
            }}
          >
            <ProgressivePullRequestDiffEntry
              diffManuallyExpanded={diffManuallyExpanded}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              onScrollToAndFocusEntry={onScrollToAndFocusEntry}
              pathDigest={pathDigest}
              repository={repository}
            />
          </div>
        )
      })}
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}
